import {Lead} from './lead.model';
import {EmploymentStatus} from './employment-status.model';
import {BlackBox} from './black-box.model';

export namespace LoanApplication {
  export interface Model {
    pk: number;
    id: number;
    __str__: string;
    lead: number;
    applied_on: Date;
    record_source: Lead.Source;
    application_source: string;
    approval_state: number;
    application_retry: number;
    to_send_confirmation: boolean;
    is_sent_confirmation: boolean;
    first_name: string;
    middle_name: string;
    last_name: string;
    birthday: Date;
    contacts: number[];
    employment_type: EmploymentType;
    employment_type_display: string;
    destined_country: Country;
    destined_country_display: string;
    job_title: string;
    job_title_link: number;
    job_category: number;
    employment_channel: EmploymentChannel;
    employment_channel_display: string;
    recruitment_agency: string;
    employer_name: string;
    manning_agency: string;
    seaman_principal: string;
    seaman_ship: string;
    employment_status: number;
    average_monthly_remittance: number;
    average_monthly_remittance_currency: string;
    datetime_to_philippines: Date;
    datetime_to_work: Date;
    datetime_contract_end: Date;
    unlimited_contract: Boolean;
    started_ofw: Date;
    monthly_salary: number;
    monthly_salary_currency: string;
    target_loan_amount: number;
    target_loan_amount_currency: string;
    target_loan_duration: number;
    estimated_monthly_payment: number;
    estimated_monthly_payment_currency: string;
    loan_purpose: string;
    has_comaker: boolean | null;
    comaker_relation: string;
    comaker_name: string;
    comaker_mobile_number: string;
    do_score: boolean | null;
    latest_score: BlackBox.FieldValue;
    is_not_robot: boolean;
    has_accepted: boolean | null;
    rest_origin: string;
    origin_user_agent: string;
    origin_url: string;
    origin_viewport_width: number;
    origin_viewport_height: number;
    branch: number;
  }

  export interface FieldValue {
    pk: number;
    __str__: string;
  }

  export enum EmploymentType {
    land = 'land',
    sea = 'sea',
  }

  export enum EmploymentChannel {
    agency = 'agency',
    directHire = 'direct_hire',
    balikManggagawa = 'balik_manggagawa',
  }

  export enum OfwStatusOption {
    agency = 'agency',
    directHire = 'direct_hire',
    balikManggagawa = 'balik_manggagawa',
  }

  export type Country = string;

  export const Interest: { [employment_type: string]: number } = {
    [EmploymentType.land]: 0.035,
    [EmploymentType.sea]: 0.03,
  };
  export const LoanFee: { [employment_type: string]: number } = {
    [EmploymentType.land]: 0.125,
    [EmploymentType.sea]: 0.13,
  };
  export const GracePeriod: { [employment_type: string]: number } = {
    [EmploymentType.land]: 1,
    [EmploymentType.sea]: 1,
  };
  export const CheckFee = 300.00;
  export const LoanAmountBounds: { [employment_type: string]: SliderBounds } = {
    [EmploymentType.land]: {min: 30000, max: 250000, step: 5000, default: 30000},
    [EmploymentType.sea]: {min: 30000, max: 250000, step: 5000, default: 30000},
  };
  export const LoanDurationBounds: { [employment_type: string]: SliderBounds } = {
    [EmploymentType.land]: {min: 6, max: 12, step: 1, default: 6},
    [EmploymentType.sea]: {min: 3, max: 8, step: 1, default: 3},
  };

  export interface SliderBounds {
    min: number;
    max: number;
    step: number;
    default: number;
  }

  export interface SliderConfig {
    interest_rate: number;
    loan_fee_rate: number;
    grace_period: number;
    check_fee: number;
    land: SliderBounds;
    sea: SliderBounds;
  }

  export interface FinalSliderConfig {
    interest_rate: number;
    loan_fee_rate: number;
    grace_period: number;
    check_fee: number;
    target_loan_amount: {
      min: number;
      max: number;
      step: number;
    };
    target_loan_duration: {
      min: number;
      max: number;
      step: number;
    };
  }

  // Copied from old BB website; equation credit to Huyen
  export function getMonthlyAmortization(principal: number,
                                         tenor: number,
                                         interestRate: number,
                                         loanFeeRate: number,
                                         gracePeriod: number,
                                         checkFee: number = 175.00) {
    return Math.round(
      principal / tenor
      + checkFee / tenor
      + principal * loanFeeRate / tenor
      + principal * interestRate * (tenor + gracePeriod) * 30 * (12 / 365) / tenor,
    );
  }

  export interface CreateVars {
    branch: number;
    application_source: number;
    has_comaker: boolean;
    comaker_relation: string;
    comaker_name: string;
    comaker_mobile_number: string;
    has_accepted: boolean | null;
    // Loan Slider
    employment_type: EmploymentType;
    target_loan_amount: number;
    target_loan_duration: number;
    estimated_monthly_payment: number;
    slider_config: FinalSliderConfig;
    //
    loan_purpose: string;
    // Employment Details
    destined_country: Country;  // if Land based
    job_title: string;
    job_title_link: number;
    job_category: number;
    employment_channel: EmploymentChannel;
    recruitment_agency: string;
    employer_name: string;
    manning_agency: string; // if Sea based
    agency: number;
    seaman_principal: string; // if Sea based
    seaman_ship: string;
    // Employment Status
    employment_status: EmploymentStatus.KnownStatuses | string;
    monthly_salary: number;
    average_monthly_remittance: number;
    datetime_to_philippines: string;  // Date
    datetime_to_work: string;  // Date
    datetime_contract_end: string;  // Date
    unlimited_contract: boolean;
    started_ofw: string;  // Date
    tentative_departure_date: string;  // Date
    // Personal
    first_name: string;
    middle_name: string;
    last_name: string;
    suffix: string;
    birthday: string;
    mobile_numbers: string[];
    landline_numbers: string[];
    email_addresses: string[];
    city: number;
    province: number;
  }
}
